import React from 'react';
import { resolvesLabel } from '../util/etDate.js';
import CategoryIcon from './CategoryIcon.jsx';
import { categoryTint } from '../util/categoryTint.js';

// A single yes/no prediction card.
//
// No live community consensus (iOS-only, plan §5). Double-down shipped
// 2026-07-08 (reverses the prior "web drops it" call) — `isDoubleDown` +
// `onToggleDoubleDown` below. `disabled` is used both for an in-flight write
// and for the post-submit LOCKED state: once the user submits their six, the
// YES/NO picks become read-only (the 2× chip and Change-answer link stay
// live post-submit — see Play.jsx for the move/change wiring).
export default function QuestionCard({
  question,
  index,
  onAnswer, // (answer:boolean) => void
  onShare, // optional () => void — when set (post-submit), a gold share icon
  // overlays the image's top-right so the user can invite a friend to predict
  // this question (→ the apex /q/<id> door).
  disabled = false,
  isDoubleDown = false,
  onToggleDoubleDown, // optional () => void — omit to hide the 2× chip entirely
  onChangeAnswer, // optional () => void — post-submit "Change answer" affordance
  onContext, // optional () => void — opens the ContextModal (web Dig Deeper).
  // The caller should only wire it when the question HAS a blurb; the button
  // renders on `onContext && question.contextBlurb` so either side can gate.
  hideImage = false, // answer-first accept cards (AF-2, Tom 2026-07-31: "the
  // first question/accept screen should not include the question image") —
  // suppresses the art AND the section tile so the card is question + YES/NO.
}) {
  const q = question;
  const resolved = q.isResolved && q.correctAnswer !== null;

  const yesSelected = q.answer === true;
  const noSelected = q.answer === false;
  const showDoubleDownChip = !resolved && !q.isSkipped && !!onToggleDoubleDown;

  return (
    <div className="ps-card" style={{ marginBottom: 14, position: 'relative' }}>
      {onShare && (
        <button onClick={onShare} aria-label="Share this question" style={shareBtn}>
          <ShareIcon />
        </button>
      )}
      {/* BACK SECTION (lane:"section") — no illustration. Section questions
          are generated in volume (18/day at target) and don't carry authored
          art, so instead of the image the slot becomes a category tile: the
          category icon on its own colour, filling the same 4:3 box the art
          would. The card is otherwise a normal daily-six card (Tom 2026-07-28).

          The icon and the word do the work, not the hue — the tint is
          reinforcement, never the only signal. */}
      {hideImage ? null : q.lane === 'section' ? (
        <div style={{ ...sectionTile, background: categoryTint(q.category) }}>
          <div style={{ color: '#ffffff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <CategoryIcon category={q.category} size={54} />
            <span style={sectionTileLabel}>{q.category}</span>
          </div>
        </div>
      ) : q.imageURL && (
        <img
          src={q.imageURL}
          alt={q.category ? `${q.category} question illustration` : ''}
          loading="lazy"
          onError={(e) => {
            // External URL — degrade gracefully if it 404s / blocks hotlinking.
            e.currentTarget.style.display = 'none';
          }}
          style={{
            // 16:9 + cover (= iOS .fill), 2026-07-30 — the Auto-Pilot engine
            // renders natively 16:9 (geminiImageClient.ts pins the ratio), and
            // every iOS question-image slot moved to 16:9 fill the same day.
            // The old 4:3+contain comment cited the iOS decision this
            // reversed. Cover, not contain: pre-pin square assets crop
            // slightly rather than letterboxing against --surface.
            width: '100%',
            aspectRatio: '16 / 9',
            objectFit: 'cover',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 12,
            display: 'block',
            background: 'var(--surface)',
          }}
        />
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10,
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="ps-chip">
            {typeof index === 'number' ? `${index + 1} · ` : ''}
            {q.category}
          </span>
          {showDoubleDownChip && (
            <DoubleDownChip active={isDoubleDown} onClick={onToggleDoubleDown} />
          )}
        </div>
        <span className="ps-chip" style={{ color: 'var(--gold)' }}>
          {resolved
            ? q.isVoided
              ? 'Voided'
              : 'Resolved'
            : resolvesLabel(q.estimatedResolutionDate)}
        </span>
      </div>

      {/* "[creator] asks" attribution (Follow mode F3) — links to the creator's
          follow page. */}
      {q.askedByCreator?.handle && (
        <a
          href={`https://predictsixapp.com/f/${q.askedByCreator.handle}`}
          target="_blank"
          rel="noopener"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--gold)', textDecoration: 'none', margin: '0 0 6px' }}
        >
          <span aria-hidden="true">👁️</span>@{q.askedByCreator.handle} asks
        </a>
      )}
      <p
        style={{
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1.32,
          margin: '0 0 14px',
        }}
      >
        {q.text}
      </p>

      {resolved ? (
        <ResolvedRow question={q} />
      ) : q.isSkipped && disabled ? (
        // Skipped + locked: an em-dash pill where the YES/NO row was — the
        // same badge iOS renders for a skip (never the word "SKIP"; the dash
        // reads as "no call", which is exactly what a skip is).
        <div style={skipRow} aria-label="Skipped — no prediction made">
          <span style={skipDash} aria-hidden="true">—</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--secondary-text)' }}>
            Skipped
          </span>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10 }}>
            <Choice
              label="YES"
              selected={yesSelected}
              disabled={disabled}
              onClick={() => onAnswer?.(true)}
            />
            <Choice
              label="NO"
              selected={noSelected}
              disabled={disabled}
              onClick={() => onAnswer?.(false)}
            />
          </div>
          {disabled && !q.isSkipped && onChangeAnswer && (
            <button
              onClick={onChangeAnswer}
              style={{
                display: 'block',
                margin: '10px auto 0',
                padding: 0,
                border: 'none',
                background: 'none',
                color: 'var(--secondary-text)',
                fontSize: 12,
                fontWeight: 700,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Change answer
            </button>
          )}
        </>
      )}

      {/* The CONTEXT button (Tom 2026-07-30: "yes, no, and context buttons
          that launch pop up modals") — web's door to the Dig Deeper modal,
          both sides of submit. Gated on the blurb: a context button with
          nothing behind it would be a broken promise, so no blurb → no
          button, silently (iOS behavior). */}
      {onContext && q.contextBlurb && (
        <button onClick={onContext} style={contextBtn} aria-label="Read the context behind this question">
          <span aria-hidden="true" style={{ fontSize: 13 }}>ⓘ</span>
          Context
        </button>
      )}
    </div>
  );
}

// The 2× double-down toggle chip — gold + filled when this is the user's
// pick for the day, outlined otherwise. Tappable while unlocked (initial
// pick) and while locked-but-unresolved (moving the pick — Play.jsx wires
// the DD-move flow through the same onToggleDoubleDown for a locked card).
function DoubleDownChip({ active, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      aria-label={active ? 'Double-down pick — tap to remove' : 'Mark as your double-down pick'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3px 8px',
        borderRadius: 999,
        border: `1.5px solid var(--gold)`,
        background: active ? 'var(--gold)' : 'transparent',
        color: active ? '#1a1a1a' : 'var(--gold)',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.02em',
        cursor: 'pointer',
        lineHeight: 1,
      }}
    >
      2×
    </button>
  );
}

// iOS-style share glyph (square.and.arrow.up), gold via currentColor.
function ShareIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 12v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    </svg>
  );
}

// The back-section stand-in for question art: same 16:9 slot, same radius and
// bottom margin as the <img> above, so a section card sits in a mixed ballot
// at exactly the same rhythm as the daily six around it.
const sectionTile = {
  width: '100%',
  aspectRatio: '16 / 9',
  borderRadius: 'var(--radius-sm)',
  marginBottom: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const sectionTileLabel = {
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: '0.10em',
  textTransform: 'uppercase',
};

// Skip badge row — centered, quiet. The pill mirrors the Choice buttons'
// geometry at reduced weight so the slot keeps its rhythm in the stack.
const skipRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '12px 0',
  borderRadius: 'var(--radius-sm)',
  border: '1.5px solid var(--border)',
  background: 'var(--surface)',
};
const skipDash = {
  fontSize: 16,
  fontWeight: 800,
  color: 'var(--secondary-text)',
  lineHeight: 1,
};

// Full-width ghost button under the answer row — subordinate to YES/NO by
// design (it informs the call, it isn't one).
const contextBtn = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  marginTop: 10,
  padding: '9px 0',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--secondary-text)',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '0.02em',
  cursor: 'pointer',
};

const shareBtn = {
  position: 'absolute',
  top: 10,
  right: 10,
  zIndex: 2,
  width: 34,
  height: 34,
  borderRadius: 17,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--gold)',
  background: 'rgba(13,13,13,0.55)',
  backdropFilter: 'blur(2px)',
  color: 'var(--gold)',
  cursor: 'pointer',
};

function Choice({ label, selected, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      style={{
        flex: 1,
        padding: '14px 0',
        borderRadius: 'var(--radius-sm)',
        border: `1.5px solid ${selected ? 'var(--gold)' : 'var(--border)'}`,
        background: selected ? 'var(--gold)' : 'var(--surface)',
        color: selected ? '#1a1a1a' : 'var(--primary-text)', // near-black on gold (A2)
        fontWeight: 800,
        fontSize: 16,
        letterSpacing: '0.04em',
        // When locked/disabled, keep the chosen pick vivid; only dim the
        // un-chosen option so the user's submitted answer reads clearly.
        opacity: disabled && !selected ? 0.45 : 1,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background 120ms, border-color 120ms, opacity 120ms',
      }}
    >
      {label}
    </button>
  );
}

function ResolvedRow({ question: q }) {
  if (q.isVoided) {
    return (
      <p className="ps-muted" style={{ margin: 0 }}>
        This question was voided — no outcome.
      </p>
    );
  }
  const outcome = q.correctAnswer ? 'YES' : 'NO';
  const userGotIt =
    typeof q.answer === 'boolean' && q.answer === q.correctAnswer;
  return (
    <div>
      <p style={{ margin: '0 0 6px', fontWeight: 800 }}>
        Resolved: <span style={{ color: 'var(--gold)' }}>{outcome}</span>
      </p>
      {q.revealHeadline && (
        <p className="ps-muted" style={{ margin: '0 0 6px', lineHeight: 1.4 }}>
          {q.revealHeadline}
        </p>
      )}
      {typeof q.answer === 'boolean' && (
        <p
          style={{
            margin: 0,
            fontWeight: 700,
            color: userGotIt ? 'var(--gold)' : 'var(--secondary-text)',
          }}
        >
          {userGotIt
            ? 'You called it.'
            : 'You said ' + (q.answer ? 'YES' : 'NO') + '.'}
        </p>
      )}
    </div>
  );
}
